import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ApiClientService } from './api-client.service';
import { CustomStyleService } from './custom-style.service';
import { CookieService } from './cookie.service';
import { environment } from '../../environments/environment';
import { catchError, filter, map, take } from 'rxjs/operators';
import { QUERY_FIELDS } from './url.service.data';
import { UrlService } from './url.service';
import { AuthTokenService } from './auth-token.service';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/never';
import { IMe } from '../types/api-response/auth/me';
import {Http} from '@angular/http';
import { AuthApiService } from './api/auth-api.service';
import { UserType } from '../types/api-response/auth/user-type';
import { ReplaySubject } from 'rxjs/ReplaySubject';

declare const swal: any;

const MCCV2_HANDOFF_PREFIX = 'https://mccv2.maxcondoclub.com/v1/auth/handoff?code=';

/** Only follow auth's v2 handoff link, never an arbitrary URL. */
export function isMccv2HandoffUrl(url?: string): boolean {
    return typeof url === 'string'
        && url.indexOf(MCCV2_HANDOFF_PREFIX) === 0
        && /^[A-Za-z0-9_-]+$/.test(url.slice(MCCV2_HANDOFF_PREFIX.length));
}

@Injectable()
export class AuthenticationService {

    public user: IMe;
    private userUpdate = new ReplaySubject<IMe>(1);
    public user$: Observable<IMe> = this.userUpdate.pipe(
        filter(user => !!user),
    );

    public userIsResident = true;

    constructor(
      private api: ApiClientService,
      public urlService: UrlService,
      private customStyle: CustomStyleService,
      public router: Router,
      private cookieService: CookieService,
      private authTokenService: AuthTokenService,
      private authApiService: AuthApiService
    ) {
        // update user details on token update
        this.authTokenService.tokenUpdate$.subscribe(() => this.getUser());

        // Initial user load if has token in localstorage
        // and authorization no needed
        if (this.isAuthorized()) {
            this.getUser();
        }

        api.onLogout.subscribe(logoutUrl => {
            this.clearDataFromStorage();

            // Redirect on logout
            if(logoutUrl) {
                window.location.href = logoutUrl;
            } else {
                this.router.navigate(['/login']);
            }
        });

        // Redirect user to login page on token error
        this.authTokenService.tokenError$.subscribe(
          (errorMessage) => this.redirectToLoginPage(errorMessage)
        );
    }

    public login(email: string, password: string): Observable<any> {
        email = encodeURIComponent(email);
        password = encodeURIComponent(password);

        return this.authTokenService.authenticateWithPassword(email, password).map(token => {
            if (token && isMccv2HandoffUrl(token.mccv2_url)) {
                window.location.href = token.mccv2_url;

                return Observable.never();
            }

            if (token) {
                this.authTokenService.setAccessToken(token);

                return this.user$;
            }

            return Observable.throw(false);
        });
    }

    /**
     * Emmit logout event
     *
     * @param logoutUrl
     */
    public logout(logoutUrl = null) {
        this.api.onLogout.emit(logoutUrl);
    }

    /**
     * Removes all stored personal data from client browser
     */
    private clearDataFromStorage() {
        this.authTokenService.removeAccessToken();
        this.userUpdate.next(null); // clear observable buffer
        this.user = null;
        localStorage.removeItem('userData');
        localStorage.removeItem('residentselectedBuilding');
        localStorage.removeItem('residentBuildings');
        this.urlService.queryFields = JSON.parse(JSON.stringify(QUERY_FIELDS));
    }

    public isAuthorized(): boolean {
        const token = this.authTokenService.accessToken;
        return !!token;
    }

    /**
     * Retrieves current user details
     */
    public getUser(): Observable<IMe> {
        this.authApiService.getMyProfile({expand: 'theme'})
            .pipe(take(1))
            .subscribe(user => {
                this.userIsResident = (user.userType === UserType.resident);
                this.saveUser(user);
                this.userUpdate.next(user);
            }, error => console.error('Failed to get user details', error));

        return this.user$;
    }

    /**
     * Sets current user details to local storage
     *
     * @param user
     */
    private saveUser(user: IMe): void {
        localStorage.setItem('userData', JSON.stringify(user));
        this.initUser();
    }

    /**
     * Returns saved user details from local storage+
     */
    getSavedUser(): IMe {
        const userDataStr = localStorage.getItem('userData');

        if(!userDataStr) {
            throw new Error('Empty property \'userData\' in local storage');
        }

        return JSON.parse(userDataStr);
    }

    private initUser() {
        const userData = localStorage.getItem('userData');
        try {
            if (userData) {
                this.user = JSON.parse(userData);
                const residentBuilding = localStorage.getItem('residentselectedBuilding') ? JSON.parse(localStorage.getItem('residentselectedBuilding')) : null;
                const buildingPropertyManager =  residentBuilding ? this.user.buildingPropertyManager.find(m => m.buildingId == residentBuilding.buildingId) : this.user.buildingPropertyManager[0];
                const theme = buildingPropertyManager ? buildingPropertyManager.theme : null;
                if (this.user.company && this.user.company.theme) this.customStyle.enableTheme(this.user.company.theme);
                if (theme && (this.userIsResident || (!this.userIsResident && this.user.buildingPropertyManager.length === 1))) this.customStyle.enableTheme(theme);
            }
        } catch (e) {
            console.error(e, 'Error get user from localStorage');
        }
    }

    isUserAdmin() {
        return this.user && this.user.userType === 'super_admin';
    }

    public sendActivityUser(): Observable<any> {
        if (!this.isAuthorized()) {
            return Observable.of(null);
        }
        return this.api.post('oauthUrl', '/api/users/me/activity.json', {});
    }

    /**
     * Redirects user to login page with popup message
     */
    public redirectToLoginPage(errorMessage?: string) {
        const config = {
            title: errorMessage ? errorMessage : 'Your session is expired.',
            text: 'Please login one more time.',
            type: 'warning',
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'OK',
            closeOnConfirm: true,
            customClass: 'swal-wide',
        };
        swal(config, () => this.logout());
    }
}

@Injectable()
export class AuthGuard implements CanActivate {

    constructor(
      private router: Router,
      private authTokenService: AuthTokenService
    ) {
    }

    canActivate() {
        if (this.authTokenService.accessToken) {
            return true;
        }
        this.router.navigate(['/login']);
        return false;
    }
}


@Injectable()
export class GuestGuard implements CanActivate {

    constructor(private authService: AuthenticationService) {}

    canActivate() {
        return !this.authService.isAuthorized();
    }
}

@Injectable()
export class LoginGuard implements CanActivate {

    constructor(private http: Http, public cs: CustomStyleService, private router: Router) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.getCompanyThemeByLoginPage(route.params.path)
            .pipe(
                map((res) => {
                    this.cs.enableTheme(res);
                    return true;
                }),
                catchError((err) => {
                    this.router.navigate(['/login']);
                    return Observable.of(false);
                })
            );

    }

    getCompanyThemeByLoginPage(loginPage): Observable<any> {
        const url = `${environment.oauthUrl}/api/company_login/${loginPage}`;
        return this.http.get(url).pipe(
            map(response => response.json())
        );
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/authentication.service.ts